/**
 * Input Sanitization Utility
 * Prevents XSS attacks and SQL injection by sanitizing user inputs
 * Use this before storing or displaying user-provided content
 */

import i18n from '../../i18n';

/**
 * Sanitize HTML content to prevent XSS attacks
 * Removes dangerous tags and attributes while preserving safe formatting
 * @param {string} html - Raw HTML string
 * @returns {string} Sanitized HTML
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return '';
  
  // Create a temporary div element
  const temp = document.createElement('div');
  temp.textContent = html;
  
  // Get the text content (this strips all HTML)
  let sanitized = temp.innerHTML;
  
  // Allow only safe HTML tags for rich text (if needed)
  // For now, we'll strip all HTML to be extra safe
  // If you need to allow some tags, use a library like DOMPurify
  
  return sanitized;
};

/**
 * Sanitize text input (removes HTML and scripts)
 * @param {string} input - Raw input string
 * @returns {string} Sanitized text
 */
export const sanitizeText = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  // Remove script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove event handlers
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
};

/**
 * Sanitize number input
 * @param {any} input - Input value
 * @param {Object} options - Options {min, max, decimals, allowNegative}
 * @returns {number|null} Sanitized number or null if invalid
 */
export const sanitizeNumber = (input, options = {}) => {
  const {
    min = null,
    max = null,
    decimals = null,
    allowNegative = true
  } = options;
  
  // Convert to number
  let num = parseFloat(input);
  
  // Check if valid number
  if (isNaN(num) || !isFinite(num)) {
    return null;
  }
  
  // Check negative
  if (!allowNegative && num < 0) {
    num = 0;
  }
  
  // Apply min/max
  if (min !== null && num < min) {
    num = min;
  }
  if (max !== null && num > max) {
    num = max;
  }
  
  // Apply decimal places
  if (decimals !== null) {
    num = parseFloat(num.toFixed(decimals));
  }
  
  return num;
};

/**
 * Sanitize email address
 * @param {string} email - Email address
 * @returns {string|null} Sanitized email or null if invalid
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return null;
  
  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Trim and lowercase
  const sanitized = email.trim().toLowerCase();
  
  // Validate
  if (!emailRegex.test(sanitized)) {
    return null;
  }
  
  // Additional safety: remove any HTML
  return sanitizeText(sanitized);
};

/**
 * Sanitize phone number (international formats supported)
 * @param {string} phone - Phone number
 * @returns {string} Sanitized phone number
 */
export const sanitizePhone = (phone) => {
  if (typeof phone !== 'string') return '';
  
  // Remove all non-numeric characters except + at start
  let sanitized = phone.replace(/[^\d+]/g, '');
  
  // Ensure + only at start
  if (sanitized.startsWith('+')) {
    sanitized = '+' + sanitized.slice(1).replace(/\+/g, '');
  }
  
  return sanitized;
};

/**
 * Sanitize URL
 * @param {string} url - URL string
 * @returns {string|null} Sanitized URL or null if invalid
 */
export const sanitizeURL = (url) => {
  if (typeof url !== 'string') return null;
  
  // Trim whitespace
  const trimmed = url.trim();
  
  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  const lowerUrl = trimmed.toLowerCase();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return null;
    }
  }
  
  // Validate URL format
  try {
    const urlObj = new URL(trimmed);
    // Only allow http and https
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return null;
    }
    return urlObj.href;
  } catch (e) {
    // Not a valid URL, return null
    return null;
  }
};

/**
 * Sanitize SQL-like input (prevent SQL injection)
 * @param {string} input - Input string
 * @returns {string} Sanitized string
 */
export const sanitizeSQLInput = (input) => {
  if (typeof input !== 'string') return '';
  
  // Remove SQL keywords and dangerous characters
  let sanitized = input;
  
  // Escape single quotes (most important for SQL injection)
  sanitized = sanitized.replace(/'/g, "''");
  
  // Remove SQL comments
  sanitized = sanitized.replace(/--/g, '');
  sanitized = sanitized.replace(/\/\*/g, '');
  sanitized = sanitized.replace(/\*\//g, '');
  
  // Remove dangerous SQL keywords (case insensitive)
  const dangerousKeywords = [
    'DROP', 'DELETE', 'TRUNCATE', 'EXEC', 'EXECUTE',
    'UNION', 'INSERT', 'UPDATE', 'ALTER', 'CREATE'
  ];
  
  for (const keyword of dangerousKeywords) {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    sanitized = sanitized.replace(regex, '');
  }
  
  return sanitized;
};

/**
 * Sanitize file name
 * @param {string} filename - File name
 * @returns {string} Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (typeof filename !== 'string') return '';
  
  // Remove path traversal attempts
  let sanitized = filename.replace(/\.\./g, '');
  sanitized = sanitized.replace(/[\/\\]/g, '');
  
  // Remove dangerous characters
  sanitized = sanitized.replace(/[<>:"|?*]/g, '');
  
  // Trim whitespace and dots
  sanitized = sanitized.trim().replace(/^\.+/, '');
  
  // Limit length
  if (sanitized.length > 255) {
    sanitized = sanitized.substring(0, 255);
  }
  
  return sanitized;
};

/**
 * Sanitize object (recursively sanitize all string values)
 * @param {Object} obj - Object to sanitize
 * @param {Function} sanitizer - Sanitizer function to use (default: sanitizeText)
 * @returns {Object} Sanitized object
 */
export const sanitizeObject = (obj, sanitizer = sanitizeText) => {
  if (obj === null || obj === undefined) return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, sanitizer));
  }
  
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        sanitized[key] = sanitizeObject(obj[key], sanitizer);
      }
    }
    return sanitized;
  }
  
  if (typeof obj === 'string') {
    return sanitizer(obj);
  }
  
  return obj;
};

/**
 * Validate and sanitize form data
 * @param {Object} formData - Form data object
 * @param {Object} schema - Validation schema
 * @returns {Object} {isValid: boolean, sanitized: Object, errors: Array}
 * 
 * @example
 * const schema = {
 *   name: { type: 'text', required: true, maxLength: 100 },
 *   email: { type: 'email', required: true },
 *   age: { type: 'number', min: 0, max: 150 },
 *   website: { type: 'url', required: false }
 * };
 * 
 * const result = validateAndSanitize(formData, schema);
 * if (result.isValid) {
 *   // Use result.sanitized
 * } else {
 *   // Show result.errors
 * }
 */
export const validateAndSanitize = (formData, schema) => {
  const errors = [];
  const sanitized = {};
  
  for (const field in schema) {
    const rules = schema[field];
    const value = formData[field];
    
    // Check required
    if (rules.required && (value === null || value === undefined || value === '')) {
      errors.push({ field, message: i18n.t('common.validation_field_required', { field }) });
      continue;
    }
    
    // Skip if not required and empty
    if (!rules.required && (value === null || value === undefined || value === '')) {
      sanitized[field] = value;
      continue;
    }
    
    // Sanitize based on type
    switch (rules.type) {
      case 'text':
        sanitized[field] = sanitizeText(value);
        
        // Check length
        if (rules.maxLength && sanitized[field].length > rules.maxLength) {
          errors.push({ field, message: i18n.t('common.validation_field_max_length', { field, max: rules.maxLength }) });
        }
        if (rules.minLength && sanitized[field].length < rules.minLength) {
          errors.push({ field, message: i18n.t('common.validation_field_min_length', { field, min: rules.minLength }) });
        }
        break;
        
      case 'email':
        sanitized[field] = sanitizeEmail(value);
        if (sanitized[field] === null) {
          errors.push({ field, message: i18n.t('common.validation_field_email', { field }) });
        }
        break;
        
      case 'number':
        sanitized[field] = sanitizeNumber(value, {
          min: rules.min,
          max: rules.max,
          decimals: rules.decimals,
          allowNegative: rules.allowNegative
        });
        if (sanitized[field] === null) {
          errors.push({ field, message: i18n.t('common.validation_field_number', { field }) });
        }
        break;
        
      case 'phone':
        sanitized[field] = sanitizePhone(value);
        break;
        
      case 'url':
        sanitized[field] = sanitizeURL(value);
        if (rules.required && sanitized[field] === null) {
          errors.push({ field, message: i18n.t('common.validation_field_url', { field }) });
        }
        break;
        
      case 'html':
        sanitized[field] = sanitizeHTML(value);
        break;
        
      default:
        sanitized[field] = sanitizeText(value);
    }
  }
  
  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};

/**
 * Escape HTML entities
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export const escapeHTML = (str) => {
  if (typeof str !== 'string') return '';
  
  const htmlEntities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;'
  };
  
  return str.replace(/[&<>"'\/]/g, (char) => htmlEntities[char]);
};

/**
 * Unescape HTML entities
 * @param {string} str - String to unescape
 * @returns {string} Unescaped string
 */
export const unescapeHTML = (str) => {
  if (typeof str !== 'string') return '';
  
  const temp = document.createElement('textarea');
  temp.innerHTML = str;
  return temp.value;
};

export default {
  sanitizeHTML,
  sanitizeText,
  sanitizeNumber,
  sanitizeEmail,
  sanitizePhone,
  sanitizeURL,
  sanitizeSQLInput,
  sanitizeFilename,
  sanitizeObject,
  validateAndSanitize,
  escapeHTML,
  unescapeHTML
};

